/**
 * usePWA — VÉRAULT Member Suite PWA hook (Wave 30 · Phase 7a)
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for install + standalone state across
 * the portal. Captures `beforeinstallprompt`, exposes a calm
 * `promptInstall()`, and tracks display-mode changes (e.g. user
 * launches the installed app, or removes it).
 *
 * Surfaces:
 *   - canInstall: deferred install event available
 *   - isStandalone: running in installed PWA mode (display: standalone)
 *   - isIos: iOS Safari (needs A2HS instructions — no native prompt)
 *   - hasDismissed: user has explicitly dismissed the prompt
 *   - promptInstall(): fires the deferred native prompt
 *   - dismissInstall(): records dismissal in localStorage
 *   - reset(): clears the dismissal (for testing)
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "vt-pwa-install-dismissed";

function detectStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true;
  } catch {
    /* noop */
  }
  return false;
}

function detectIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  return isIosDevice;
}

export function usePWA() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => detectStandalone());
  const [hasDismissed, setHasDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
    };
    const onInstalled = () => {
      setDeferredEvent(null);
      setIsStandalone(true);
    };
    const onDisplayChange = () => setIsStandalone(detectStandalone());

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    let mq;
    try {
      mq = window.matchMedia("(display-mode: standalone)");
      mq.addEventListener
        ? mq.addEventListener("change", onDisplayChange)
        : mq.addListener(onDisplayChange);
    } catch {
      /* noop */
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (mq) {
        try {
          mq.removeEventListener
            ? mq.removeEventListener("change", onDisplayChange)
            : mq.removeListener(onDisplayChange);
        } catch {
          /* noop */
        }
      }
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return { outcome: "unavailable" };
    try {
      deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      setDeferredEvent(null);
      if (choice && choice.outcome === "accepted") {
        setIsStandalone(true);
      }
      return { outcome: (choice && choice.outcome) || "dismissed" };
    } catch {
      return { outcome: "error" };
    }
  }, [deferredEvent]);

  const dismissInstall = useCallback(() => {
    setHasDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
  }, []);

  const reset = useCallback(() => {
    setHasDismissed(false);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }, []);

  return {
    canInstall: !!deferredEvent,
    isStandalone,
    isIos: detectIos(),
    hasDismissed,
    promptInstall,
    dismissInstall,
    reset,
  };
}

export default usePWA;
